import React from "react";
import { IFieldBaseProps } from "common/components/Field";
import { P } from "common/components/Paragraph";
import { useLocation } from "react-router";

export interface ICheckboxControlProps extends IFieldBaseProps<"checkbox" | "checkboxGroup"> {
  size?: "sm" | "md";
  label: string;
  onChange: (checked: boolean, name: string) => void;
  isChecked: boolean;
  namespace?: string;
  border?: boolean;
  hideLabel?: boolean;
  describedBy?: string;
  noBackground?: boolean;
}

export const CheckboxControl: React.FC<ICheckboxControlProps> = ({
  label,
  name,
  disabled,
  size = "md",
  onChange,
  isChecked,
  namespace = "",
  type,
  border = false,
  hideLabel = false,
  describedBy,
  noBackground = false,
}) => {
  // Hooks
  const { hash } = useLocation();
  const secondHash = hash?.split("#")[2];
  const isHash = secondHash?.replaceAll("-", " ") === label.toLowerCase().replaceAll("-", "");

  // Constants
  const sizes = {
    sm: "h-xl",
    md: "h-2xl",
  };
  const iconSizes = {
    sm: "text-base",
    md: "text-lg",
  };
  const ownClassName = `flex w-fit ${isHash ? "bg-primary-50" : ""}`;
  let wrapperClassName = `flex items-center gap-xs px-md py-xs rounded-3xl
  border border-secondary-200 text-secondary-900 select-none group
  peer-focus:ring-[3px] peer-focus:ring-secondary-50 focus:outline-none
  peer-disabled:cursor-not-allowed peer-disabled:text-secondary-500 peer-disabled:border-secondary-50 peer-enabled:cursor-pointer ${sizes[size]}`;

  // Local State
  const contextName = namespace + (namespace ? "-" : "") + name;
  let iconClassName = `material-symbols-outlined ${iconSizes[size]} ${
    disabled ? "!text-secondary-300" : isChecked ? "text-secondary-900" : "text-secondary-300"
  } group-hover:text-secondary-900`;

  const icon = isChecked ? "check_box" : "check_box_outline_blank";
  if (isChecked) {
    iconClassName = iconClassName.concat(" ", "filled");
  }

  if (!border) {
    wrapperClassName = wrapperClassName.concat(" ", "border-none");
  }

  if (!noBackground) {
    wrapperClassName = `${wrapperClassName} peer-enabled:hover:bg-secondary-50 peer-enabled:hover:border-secondary-400`;
  }

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked, name);
  };

  // Lifecycle
  return (
    <label htmlFor={contextName} className={ownClassName}>
      <input
        id={contextName}
        name={name}
        checked={isChecked}
        type="checkbox"
        disabled={disabled}
        className="hidden peer"
        aria-describedby={describedBy}
        onChange={onChangeHandler}
      />
      <div className={wrapperClassName}>
        <span className={iconClassName}>{icon}</span>
        <P size={size} className={hideLabel ? "sr-only" : ""}>
          {label}
        </P>
      </div>
    </label>
  );
};
